import React, { useMemo, useState } from 'react';
import moment from 'moment-timezone';

import TransactionsKpiBar from '../../../components/TransactionsKpiBar';
import CampaignRangeSelector from '../../../components/CampaignRangeSelector';
import TransactionsChart from './TransactionsChart';
import { useTimezone } from '../../../../../../../core/hooks/useTimezone';
import PerformanceCardSkeleton from '../../../components/PerformanceCardSkeleton';
import PerformanceCardError from '../../../components/PerformanceCardError';

// =============================================================================
// TransactionsPerformance (V2 tab) — unified Performance card.
//
// One section wrapping: header (eyebrow + range selector), KPI bar, and the
// trend chart. Mirrors the Summary / Display / V1-Transactions pattern so
// all four tabs read identically.
//
// Props mirror what TransactiosTab.js already has on hand:
//   data              detailBarSummary — KPI bar values
//   byDate, byDayOfWeek   chart data (V2 shape: row.date / row.dayOfWeek)
//   defaultSummaryDates   [startMoment, endMoment] — full data range, used
//                         as the "Custom" pill fallback label
//   changeDatePicker      callback from Controller; invoked with a [start, end]
//                         pair when the header range selector writes
//   loading               boolean — spins the KPI + chart body while refetching
//   error                 boolean — the data behind the card failed to load;
//                         shows an error body (with Retry) instead of zeros
//   onRetry               callback for the error body's Retry button
//   priceVisible          gates Spend / ROAS KPIs
//   dataConfig            org config (for ROAS format default)
//   currencyFormat        org currency
// =============================================================================

const TransactionsPerformance = ({
  data,
  byDate,
  byDayOfWeek,
  defaultSummaryDates,
  changeDatePicker,
  loading = false,
  error = false,
  onRetry,
  priceVisible = true,
  dataConfig,
  currencyFormat,
}) => {
  const { timezone: effectiveTimezone = 'UTC' } = useTimezone();
  const [rangeType, setRangeType] = useState('Custom');
  // Tracks the currently-selected date range locally so the pills reflect
  // the user's pick. Initial value = defaultSummaryDates (full data range).
  const [selectedRange, setSelectedRange] = useState(() =>
    defaultSummaryDates && defaultSummaryDates.length === 2
      ? [moment(defaultSummaryDates[0]), moment(defaultSummaryDates[1])]
      : []
  );

  const defaultDateRange = useMemo(() => {
    if (!defaultSummaryDates || defaultSummaryDates.length !== 2) return null;
    return [moment(defaultSummaryDates[0]), moment(defaultSummaryDates[1])];
  }, [defaultSummaryDates]);

  if (loading) return <PerformanceCardSkeleton />;

  return (
    <section className="campaign-overview__performance">
      <header className="campaign-overview__performance-head">
        <div className="campaign-overview__performance-eyebrow mono-eyebrow">
          Performance
        </div>
        <CampaignRangeSelector
          rangeType={rangeType}
          byCampaignRangeDate={selectedRange}
          setRangeType={setRangeType}
          setByCampaignRangeDate={(newRange) => {
            setSelectedRange(newRange);
            if (changeDatePicker) changeDatePicker(newRange);
          }}
          timezone={effectiveTimezone}
          defaultDateRange={defaultDateRange}
        />
      </header>

      {error ? (
        <PerformanceCardError
          description="The attributed transactions for this filter failed to load, so these numbers cannot be shown."
          onRetry={onRetry}
        />
      ) : (
        <>
          <TransactionsKpiBar
            data={data}
            priceVisible={priceVisible}
            dataConfig={dataConfig}
            currencyFormat={currencyFormat}
          />

          <TransactionsChart byDate={byDate} byDayOfWeek={byDayOfWeek} />
        </>
      )}
    </section>
  );
};

export default TransactionsPerformance;
